<template>
  <div class="contact-form">
    <translate tag="h2">Contact us</translate>
    <div class="intro-text">
      <i18n-t
        keypath="If you can't {findWhatYoureLookingForFAQLink} feel free to contact us."
      >
        <template #findWhatYoureLookingForFAQLink>
          <nuxt-link :to="$puLocalePath({ path: '/about/faq/' })">
            <translate>find what you're looking for in our FAQ</translate>
          </nuxt-link>
        </template>
      </i18n-t>
    </div>
    <form novalidate action="/about/contact/" @submit.prevent="send">
      <base-input
        type="text"
        name="name"
        :required="true"
        v-model="nameValue"
        :validation-rules="nameValidationSchema"
        class="contact-form-element"
      >
        <template #label>
          <translate>Name</translate>
        </template>
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules.includes('max')"
            :tt="{ max: 256, stringLength: nameValue?.length }"
            tag="div"
          >
            Ensure this value has at most {max} characters (it has
            {stringLength}).
          </translate>
          <translate v-if="failedRules.includes('required')" tag="div">
            Please tell us your name
          </translate>
        </template>
      </base-input>
      <base-input
        type="email"
        name="email"
        :required="true"
        v-model="emailValue"
        :validation-rules="emailValidationSchema"
        class="contact-form-element"
      >
        <template #label>
          <translate>Email address</translate>
        </template>
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules.includes('max')"
            :tt="{ max: 80, stringLength: emailValue?.length }"
            tag="div"
          >
            Ensure this value has at most {max} characters (it has
            {stringLength}).
          </translate>
          <translate v-if="failedRules.includes('required')" tag="div">
            Please enter your email address
          </translate>
          <translate v-if="failedRules.includes('email')" tag="div">
            Please enter a valid email address
          </translate>
        </template>
      </base-input>
      <base-input
        v-model="telephoneValue"
        name="telephone"
        type="tel"
        :required="false"
        class="contact-form-element"
        :validation-rules="phoneNumberValidationSchema"
      >
        <template #label>
          <translate>Telephone</translate>
        </template>
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules.includes('max')"
            :tt="{ max: 25, stringLength: telephoneValue?.length }"
            tag="div"
          >
            Ensure this value has at most {max} characters (it has
            {stringLength}).
          </translate>
          <translate v-if="failedRules.includes('number')" tag="div"
            >Please check your phone number</translate
          >
        </template>
      </base-input>
      <base-input
        v-if="!contactStore.isCustomerTypeCustomer"
        type="text"
        name="company"
        :required="false"
        v-model="companyValue"
        class="contact-form-element"
        :validation-rules="companyValidationSchema"
      >
        <template #label>
          <translate>Company</translate>
        </template>
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules.includes('max')"
            :tt="{ max: 256, stringLength: companyValue?.length }"
            tag="div"
          >
            Ensure this value has at most {max} characters (it has
            {stringLength}).
          </translate>
        </template>
      </base-input>
      <base-select
        v-if="prettyIds && prettyIds.length"
        name="bookingReference"
        :select-options="prettyIds"
        default-select-name="--"
        :required="true"
        css-class="booking-ref-select contact-form-element"
        @change="bookingReferenceValue = $event.selectedValue"
      >
        <template #label>
          <translate>Booking reference</translate>
        </template>
      </base-select>
      <base-input
        v-else-if="contactStore.isCustomerTypeCustomer"
        type="text"
        name="bookingReference"
        :required="false"
        v-model="bookingReferenceValue"
        class="contact-form-element"
        :validation-rules="bookingReferenceValidationSchema"
      >
        <template #label>
          <translate>Booking reference</translate>
        </template>
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules.includes('max')"
            :tt="{ max: 255, stringLength: bookingReferenceValue?.length }"
            tag="div"
          >
            Ensure this value has at most {max} characters (it has
            {stringLength}).
          </translate>
        </template>
      </base-input>
      <lazy-login-modal v-if="!isLoggedIn" class="contact-form-element">
        <translate
          tag="a"
          data-test="login-link-account-exists"
          @click.prevent="$emit('login')"
        >
          Log in
        </translate>
      </lazy-login-modal>
      <campsite-name-suggest v-model="campsiteNameValue">
        <template v-slot:default="{ emptySuggestions, fetchSuggestions }">
          <base-input
            type="text"
            name="campsiteName"
            :required="false"
            v-model="campsiteNameValue"
            class="contact-form-element"
            autocomplete="off"
            autocorrect="off"
            spellcheck="false"
            :validation-rules="campsiteNameValidationSchema"
            @focus="fetchSuggestions"
            @blur="emptySuggestions"
          >
            <template #label>
              <translate>Name of campsite or holiday park</translate>
            </template>
            <template #errorMessage="{ failedRules }">
              <translate
                v-if="failedRules.includes('max')"
                :tt="{ max: 200, stringLength: campsiteNameValue?.length }"
                tag="div"
              >
                Ensure this value has at most {max} characters (it has
                {stringLength}).
              </translate>
            </template>
          </base-input>
        </template>
      </campsite-name-suggest>
      <base-textarea
        v-model="commentsValue"
        :required="true"
        :rows="4"
        :autogrow="false"
        name="comments"
        :validation-rules="commentsValidationSchema"
        css-class="asblock contact-form-element"
        autocorrect="off"
      >
        <template #label>
          <translate>Your comments</translate>
        </template>
        <template #errorMessage="{ failedRules }">
          <translate
            v-if="failedRules.includes('max')"
            :tt="{ max: 32000, stringLength: commentsValue?.length }"
            tag="div"
          >
            Ensure this value has at most {max} characters (it has
            {stringLength}).
          </translate>
          <translate v-if="failedRules.includes('required')" tag="div">
            Please fill in this field
          </translate>
        </template>
      </base-textarea>
      <base-checkbox
        v-model="marketingValue"
        name="marketing"
        class="contact-form-element"
      >
        <translate>
          I'd be happy to receive a newsletter and occasional marketing
          communications from Pitchup.com
        </translate>
      </base-checkbox>
      <base-submit-button class="primary asblock">
        <translate>Send message to Pitchup.com</translate>
      </base-submit-button>
    </form>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
/* TODO: Add proper typing to resolve type safety warnings */
import { Pinia as _Pinia } from 'pinia'
import { useForm } from 'vee-validate'
import { z } from 'zod'
import { getLangFallbackUrl } from '../../lang/utils/langfetch'
import { UserBookingsRepositoryFetch } from '~/apps/user-bookings/UserBookingsRepositoryFetch'
import { isDatePast } from '~/utility/date/relative'
const { i18n, t } = useI18n()
const authStore = useAuthStore()

const contactStore = useContactStoreSetup(usePinia())

const { validate, setErrors } = useForm()

const nameValue = ref('')
const emailValue = ref('')
const telephoneValue = ref('')
const companyValue = ref('')
const bookingReferenceValue = ref('')
const campsiteNameValue = ref('')
const commentsValue = ref('')
const marketingValue = ref(false)

const nameValidationSchema = z
  .string()
  .transform((value) => value.trim())
  .pipe(
    z
      .string({ required_error: 'required' })
      .max(256, { message: 'max' })
      .nonempty({ message: 'required' }),
  )

const emailValidationSchema = z
  .string()
  .transform((value) => value.trim())
  .pipe(
    z
      .string({ required_error: 'required' })
      .max(80, { message: 'max' })
      .nonempty({ message: 'required' })
      .email({ message: 'email' }),
  )

const phoneNumberValidationSchema = computed(() => {
  const phoneRegex = /^[0-9 ]+$/ // only digits and spaces
  const schema = z.union([
    z
      .string()
      .max(25, { message: 'max' })
      .regex(phoneRegex, { message: 'number' }),
    z.string().length(0),
  ])
  return schema
})

const companyValidationSchema = z.string().max(256, { message: 'max' })

const campsiteNameValidationSchema = z.string().max(200, { message: 'max' })

const bookingReferenceValidationSchema = z.string().max(256, { message: 'max' })

const commentsValidationSchema = z
  .string()
  .transform((value) => value.trim())
  .pipe(
    z
      .string({ required_error: 'required' })
      .max(32000, { message: 'max' })
      .nonempty({ message: 'required' }),
  )

const isLoggedIn = computed(() => {
  return authStore.isLoggedIn
})

const { data: bookings } = useAsyncData(
  'contact-form-user-bookings',
  () => {
    const userBookingsRepo = new UserBookingsRepositoryFetch()
    return userBookingsRepo.readAll(i18n.locale)
  },
  { watch: [isLoggedIn] },
)

const { data: securityData } = await useLazyAsyncData<{
  honeypot_name: string
  security_hash: string
  timestamp: number
}>('contact-form-security-data', () => {
  return $fetch(
    getLangFallbackUrl('en-gb', '/about/api/contact/security-data/'),
  )
})

const prettyIds = computed(() => {
  const futureBookings = bookings.value?.filter((booking) => {
    return !isDatePast(booking.depart)
  })

  return futureBookings?.map((booking) => ({
    name: t(
      '{bookingPrettyId}, {bookingCampsiteName}, (Arrival date: {arrivalDate, puDate})',
      {
        bookingPrettyId: booking.prettyId,
        bookingCampsiteName: booking.campsite.name,
        arrivalDate: booking.arrive,
      },
    ),
    value: booking.prettyId,
  }))
})

async function send(event) {
  event.preventDefault()
  const isValid = (await validate()).valid
  if (!isValid || !securityData?.value) {
    return
  }
  try {
    const formData = new URLSearchParams()
    formData.append('name', nameValue.value)
    formData.append('email', emailValue.value)
    formData.append('phone', telephoneValue.value)
    // TODO find out if subject should be replaced by booking
    formData.append('subject', bookingReferenceValue.value)
    formData.append('booking', bookingReferenceValue.value)
    formData.append('campsite_name', campsiteNameValue.value || 'None')
    // TODO get id from bookingReferenceValue if exists
    formData.append('campsite_id', campsiteNameValue.value || 'None')
    formData.append('description', commentsValue.value)
    if (marketingValue.value) {
      formData.append('mailing', 'true')
    }
    formData.append('reason', contactStore.reason)
    formData.append('enquirer', contactStore.customerTypeTextValue)
    formData.append('company', companyValue.value)
    formData.append('timestamp', String(securityData?.value.timestamp))
    formData.append('security_hash', securityData?.value.security_hash)
    formData.append(securityData?.value.honeypot_name, '')

    await $fetch(getLangFallbackUrl('en-gb', '/about/api/contact/submit/'), {
      method: 'POST',
      body: formData,
      headers: {
        'x-requested-with': 'XMLHttpRequest',
      },
      redirect: 'manual',
    })
    contactStore.setFormSubmitted(true)
  } catch (error: any) {
    const errorData = error.data as {
      errors: {
        name: string
        email: string
        description: string
      }
    }
    setErrors({
      name: errorData.errors.name ? 'required' : undefined,
      email: errorData.errors.email ? 'required' : undefined,
      comments: errorData.errors.description ? 'required' : undefined,
    })
  }
}
</script>

<style scoped>
.contact-form {
  margin: 1em 0;
  padding: 1em 1em 2em;
  background: #dcf1ff;
  border-radius: 1em;
}

.intro-text {
  margin-bottom: 1em;
}

.contact-form-element {
  margin-bottom: 1em;
}

.contact-form :deep(.booking-ref-select) {
  margin-bottom: 1em;
  display: block;
}

.contact-form :deep(.booking-ref-select .select) {
  width: 100%;
  margin: 0;
}

.base-checkbox.contact-form-element {
  margin-top: 1em;
}
</style>
